import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEURPrecise, fmtDateDE } from "../lib/api";
import { LogOut, Plus, Trash2, Edit, Truck, Calendar, TrendingUp, X } from "lucide-react";

const emptyVehicle = {
  name: "", category: "Kastenwagen", description: "", price_per_day: 89, beds: 2, seats: 2,
  length_m: 5.4, transmission: "Schaltgetriebe", features: [], images: [], active: true,
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVehicle);

  useEffect(() => {
    if (!localStorage.getItem("cr_token")) { nav("/admin"); return; }
    reload();
  }, [nav]);

  const reload = async () => {
    try {
      const [v, b, s] = await Promise.all([
        api.get("/admin/vehicles"),
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
      ]);
      setVehicles(v.data); setBookings(b.data); setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("cr_token"); nav("/admin"); }
    }
  };

  const logout = () => { localStorage.removeItem("cr_token"); localStorage.removeItem("cr_user"); nav("/admin"); };

  const openNew = () => { setEditing(null); setForm(emptyVehicle); setShowForm(true); };
  const openEdit = (v) => { setEditing(v.id); setForm({ ...v }); setShowForm(true); };

  const saveVehicle = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_per_day: parseFloat(form.price_per_day),
      beds: parseInt(form.beds), seats: parseInt(form.seats), length_m: parseFloat(form.length_m),
      features: typeof form.features === "string" ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : form.features,
      images: typeof form.images === "string" ? form.images.split(",").map((s) => s.trim()).filter(Boolean) : form.images,
    };
    if (editing) await api.put(`/admin/vehicles/${editing}`, payload);
    else await api.post("/admin/vehicles", payload);
    setShowForm(false); reload();
  };

  const delVehicle = async (id) => {
    if (!window.confirm("Fahrzeug wirklich löschen?")) return;
    await api.delete(`/admin/vehicles/${id}`); reload();
  };

  const setBookingStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    reload();
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#F0EFEA]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex justify-between items-center mb-8">
          <div>
            <span className="chip">Admin</span>
            <h1 className="section-title mt-3">Verwaltung</h1>
          </div>
          <button onClick={logout} className="btn-outline" data-testid="admin-logout">
            <LogOut size={16}/> Abmelden
          </button>
        </div>

        <div className="flex gap-2 mb-8 border-b border-[#D8D6D0]">
          {[
            { k: "dashboard", l: "Übersicht" },
            { k: "vehicles", l: "Fahrzeuge" },
            { k: "bookings", l: "Buchungen" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? "border-[#C05C42] text-[#C05C42]" : "border-transparent text-[#1B2A22]/60 hover:text-[#1B2A22]"}`}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === "dashboard" && (
          <div className="grid md:grid-cols-4 gap-4" data-testid="dashboard-stats">
            {[
              { i: Truck, l: "Fahrzeuge", v: stats.total_vehicles || 0 },
              { i: Calendar, l: "Gesamt Buchungen", v: stats.total_bookings || 0 },
              { i: Calendar, l: "Bestätigt", v: stats.confirmed_bookings || 0 },
              { i: TrendingUp, l: "Umsatz", v: fmtEURPrecise(stats.revenue_eur || 0) },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#D8D6D0] p-6">
                <s.i size={20} className="text-[#C05C42]"/>
                <div className="text-xs text-[#1B2A22]/60 uppercase tracking-wider mt-4">{s.l}</div>
                <div className="font-display font-bold text-2xl mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "vehicles" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={openNew} className="btn-primary" data-testid="new-vehicle-btn"><Plus size={16}/> Neues Fahrzeug</button>
            </div>
            <div className="bg-white rounded-2xl border border-[#D8D6D0] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F0EFEA] text-left">
                  <tr>
                    <th className="p-4">Name</th>
                    <th className="p-4">Kategorie</th>
                    <th className="p-4">Preis/Tag</th>
                    <th className="p-4">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.id} className="border-t border-[#D8D6D0]" data-testid={`admin-vehicle-${v.id}`}>
                      <td className="p-4 font-medium">{v.name}</td>
                      <td className="p-4">{v.category}</td>
                      <td className="p-4">{fmtEURPrecise(v.price_per_day)}</td>
                      <td className="p-4"><span className={`chip ${v.active ? "bg-[#A9BA9D]/20 border-[#A9BA9D]" : ""}`}>{v.active ? "Aktiv" : "Inaktiv"}</span></td>
                      <td className="p-4 flex gap-2 justify-end">
                        <button onClick={() => openEdit(v)} className="p-2 hover:bg-[#F0EFEA] rounded-lg" data-testid={`edit-${v.id}`}><Edit size={14}/></button>
                        <button onClick={() => delVehicle(v.id)} className="p-2 hover:bg-[#F0EFEA] rounded-lg text-[#C05C42]" data-testid={`delete-${v.id}`}><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="bg-white rounded-2xl border border-[#D8D6D0] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EFEA] text-left">
                <tr>
                  <th className="p-4">Kunde</th>
                  <th className="p-4">Fahrzeug</th>
                  <th className="p-4">Zeitraum</th>
                  <th className="p-4">Betrag</th>
                  <th className="p-4">Zahlung</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-[#D8D6D0]" data-testid={`admin-booking-${b.id}`}>
                    <td className="p-4">
                      <div className="font-medium">{b.customer_name}</div>
                      <div className="text-xs text-[#1B2A22]/60">{b.customer_email}</div>
                    </td>
                    <td className="p-4">{b.vehicle_name}</td>
                    <td className="p-4 text-xs">{fmtDateDE(b.start_date)} – {fmtDateDE(b.end_date)}</td>
                    <td className="p-4 font-semibold">{fmtEURPrecise(b.total)}</td>
                    <td className="p-4">
                      <span className={`chip ${b.payment_status === "paid" ? "bg-[#A9BA9D]/20 border-[#A9BA9D]" : ""}`}>{b.payment_status}</span>
                    </td>
                    <td className="p-4">
                      <select value={b.status} onChange={(e) => setBookingStatus(b.id, e.target.value)} className="input-field !py-1 !text-xs" data-testid={`status-${b.id}`}>
                        <option value="pending">Ausstehend</option>
                        <option value="confirmed">Bestätigt</option>
                        <option value="cancelled">Storniert</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#1B2A22]/60">Noch keine Buchungen</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="vehicle-form-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display font-bold text-2xl">{editing ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}</h2>
              <button onClick={() => setShowForm(false)}><X size={20}/></button>
            </div>
            <form onSubmit={saveVehicle} className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="label">Name</label><input required className="input-field" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="form-name"/></div>
              <div><label className="label">Kategorie</label>
                <select className="input-field" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
                  <option>Kastenwagen</option><option>Alkoven</option><option>Teilintegriert</option><option>Vollintegriert</option>
                </select>
              </div>
              <div><label className="label">Preis / Tag (€)</label><input type="number" step="0.01" required className="input-field" value={form.price_per_day} onChange={(e) => setForm({...form, price_per_day: e.target.value})}/></div>
              <div><label className="label">Schlafplätze</label><input type="number" required className="input-field" value={form.beds} onChange={(e) => setForm({...form, beds: e.target.value})}/></div>
              <div><label className="label">Sitzplätze</label><input type="number" required className="input-field" value={form.seats} onChange={(e) => setForm({...form, seats: e.target.value})}/></div>
              <div><label className="label">Länge (m)</label><input type="number" step="0.1" required className="input-field" value={form.length_m} onChange={(e) => setForm({...form, length_m: e.target.value})}/></div>
              <div><label className="label">Getriebe</label>
                <select className="input-field" value={form.transmission} onChange={(e) => setForm({...form, transmission: e.target.value})}>
                  <option>Schaltgetriebe</option><option>Automatik</option>
                </select>
              </div>
              <div className="col-span-2"><label className="label">Beschreibung</label><textarea rows={3} className="input-field" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/></div>
              <div className="col-span-2"><label className="label">Ausstattung (kommagetrennt)</label><input className="input-field" value={Array.isArray(form.features) ? form.features.join(", ") : form.features} onChange={(e) => setForm({...form, features: e.target.value})}/></div>
              <div className="col-span-2"><label className="label">Bild-URLs (kommagetrennt)</label><textarea rows={2} className="input-field" value={Array.isArray(form.images) ? form.images.join(", ") : form.images} onChange={(e) => setForm({...form, images: e.target.value})}/></div>
              <div className="col-span-2 flex items-center gap-3"><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})}/> <span className="text-sm">Aktiv (buchbar)</span></div>
              <div className="col-span-2 flex gap-3 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1 justify-center">Abbrechen</button>
                <button type="submit" className="btn-primary flex-1 justify-center" data-testid="save-vehicle">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
